import { useGetStats } from "hooks/LabelPropagation/useGetStats";
import { useFlywheelCycle } from "./useFlywheelCycle";
import {
  ClassOption,
  StepConfig,
} from "views/components/LabelPropagation/types";
import SeedStep, {
  SeedStepRef,
} from "views/components/LabelPropagation/SeedStep";
import { CardItem } from "views/components/LabelPropagation/SelectedClassesListStep/ClassCard";
import AddClassButton from "views/components/LabelPropagation/SeedStep/AddClassButton";
import ProgressStep from "views/components/LabelPropagation/ProgressStep";
import {
  VLButtonProps,
  VLPrimaryButtonProps,
} from "views/uikit/Buttons/VLButton";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import {
  FLOW_PARAMETER,
  LABEL_CLASS_PARAMETER,
  REVIEW_PARAMETER,
  SEED_PARAMETER,
} from "../../helpers/constants/miscellaneous";
import { VLActionModalProps } from "views/uikit/VLModal/VLActionModal";
import {
  FlowStatus,
  usePollSeedStatusAndStats,
} from "./usePollSeedStatusAndStats";
import { toast } from "react-toastify";
import { VLNoActionModal } from "views/uikit/VLModal";

interface UseStepsConfigProps {
  selectedValues: string[];
  setSelectedValues: React.Dispatch<React.SetStateAction<string[]>>;
  options: ClassOption[];
  selectedCard: string | null;
  progressLimit: number;
  loading?: boolean;
  error?: any;
  allClassesMeetLimit: boolean;
  onDelete?: (value: string) => void;
  onItemDelete?: (value: string, mediaId: string) => void;
  classItemsMap?: Record<string, CardItem[]>;
  flowId?: string | null;
  seedId?: string | null;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  datasetId: string;
  requiredSeedSamplesPerLabel: number;
  setModal: Dispatch<SetStateAction<VLActionModalProps | null>>;
  seedStepRef?: React.RefObject<SeedStepRef>;
  setClassItemsMap: React.Dispatch<
    React.SetStateAction<Record<string, CardItem[]>>
  >;
  shouldFetch?: string;
}

export const useStepsConfig = ({
  selectedValues,
  setSelectedValues,
  options,
  selectedCard,
  progressLimit,
  loading,
  error,
  allClassesMeetLimit,
  onDelete,
  onItemDelete,
  classItemsMap,
  flowId,
  setStep,
  datasetId,
  requiredSeedSamplesPerLabel,
  setModal,
  seedStepRef,
  setClassItemsMap,
  shouldFetch,
}: UseStepsConfigProps): StepConfig[] => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const seedId = searchParams.get(SEED_PARAMETER);
  const isReview = searchParams.get(REVIEW_PARAMETER);
  const [active, setActive] = useState(false);

  const [doneAction, setDoneAction] = useState<() => void>();

  const [isLoading, setIsLoading] = useState(false);

  const { status, isFinalStatus } = usePollSeedStatusAndStats({
    datasetId,
    flowId: flowId ?? null,
    seedId,
    active,
  });

  const { runCycle, finishCycle, resetCycle } = useFlywheelCycle();

  const { fetchStats, stats } = useGetStats();

  const displayReviewStepNextBtn =
    stats?.review.mainProgress.itemLength &&
    stats.review.mainProgress.maxItems === stats.review.mainProgress.itemLength;

  useEffect(() => {
    if (!active || !status) return;
    if (status === FlowStatus.PAUSED_FOR_REVIEW) {
      (async () => {
        setIsLoading(true);
        try {
          if (flowId) await fetchStats(datasetId, flowId);
          const action = doneAction;
          setIsLoading(false);
          if (action) action();
        } finally {
          setIsLoading(false);
          setActive(false);
        }
      })();
    }
  }, [
    isFinalStatus,
    active,
    datasetId,
    flowId,
    fetchStats,
    doneAction,
    status,
  ]);

  const navigateToFirstStep = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete(LABEL_CLASS_PARAMETER);
    params.delete(REVIEW_PARAMETER);
    params.delete(FLOW_PARAMETER);
    params.delete(SEED_PARAMETER);
    navigate({ pathname: location.pathname, search: params.toString() });
  }, [location, navigate]);

  useEffect(() => {
    if (!active || !status) return;

    if (status === FlowStatus.COMPLETED) {
      setActive(false);
      setIsLoading(false);
      const params = new URLSearchParams(location.search);
      params.delete(SEED_PARAMETER);
      params.delete(LABEL_CLASS_PARAMETER);
      params.delete(REVIEW_PARAMETER);
      params.delete(FLOW_PARAMETER);
      const toastId = toast(
        <VLNoActionModal
          isOpen={true}
          variant={"success"}
          onClose={() => toast.dismiss(toastId)}
          headers={{
            title: "Label Propagation Completed!",
            subTitle: `${stats?.mainProgress.itemLength} Images were successfully labeled in your dataset.`,
          }}
        />,
        { autoClose: 7000, closeButton: false, closeOnClick: false },
      );
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true },
      );
    }
  }, [
    active,
    status,
    location.search,
    location.pathname,
    navigate,
    stats?.mainProgress.itemLength,
  ]);

  const onFinishClicked = useCallback(() => {
    setModal({
      onClick: async () => {
        if (!datasetId || !flowId) return;
        setModal(null);
        setIsLoading(true);
        await finishCycle(datasetId, flowId);
        setActive(true);
      },
      isOpen: true,
      variant: "info",
      onClose: () => setModal(null),
      headers: {
        title: "Ready to submit your feedback?",
        subTitle:
          "Once you submit, label propagation will stop and all labels annotated up to this point will be saved as final.",
      },
      actionLabel: "Yes, Finalize",
    });
  }, [setModal, datasetId, flowId, finishCycle]);

  const onAbortClicked = useCallback(() => {
    setModal({
      onClick: async () => {
        if (datasetId && flowId) await resetCycle(datasetId, flowId);
        navigateToFirstStep();
        setModal(null);
        setStep(1);
      },
      isOpen: true,
      variant: "error",
      onClose: () => setModal(null),
      headers: {
        title: "All progress will be lost. Are you sure?",
        subTitle:
          "Resetting removes all annotations from label propagation. Any new classes or progress made will not be saved. You’ll return to the beginning.",
      },
      actionLabel: "Reset",
    });
  }, [datasetId, flowId, resetCycle, navigateToFirstStep, setModal, setStep]);

  const getProgressStepNextBtn =
    useCallback((): VLPrimaryButtonProps | null => {
      if (isReview) {
        return {
          text: "Submit",
          type: "primary",
          isGradient: true,
          disabled: !displayReviewStepNextBtn || isLoading,
          tooltip: isLoading ? "Submitting..." : undefined,
          loading: isLoading,
          onClick: async () => {
            setIsLoading(true);
            if (!flowId || !datasetId) return;

            setDoneAction(() => async () => {
              const params = new URLSearchParams(location.search);
              params.delete(LABEL_CLASS_PARAMETER);
              setIsLoading(false);
              navigate({
                pathname: location.pathname,
                search: params.toString(),
              });
            });

            await runCycle(datasetId, flowId);
            fetchStats(datasetId, flowId);
            setActive(true);
          },
        };
      }
      return {
        text: "Finish",
        type: "primary",
        onClick: onFinishClicked,
        disabled: isLoading,
      };
    }, [
      isReview,
      onFinishClicked,
      displayReviewStepNextBtn,
      isLoading,
      flowId,
      datasetId,
      runCycle,
      fetchStats,
      location.search,
      location.pathname,
      navigate,
    ]);

  const getProgressStepPrevBtn = useCallback((): VLButtonProps | null => {
    if (!isReview)
      return {
        text: "Reset",
        type: "default",
        onClick: onAbortClicked,
        disabled: isLoading,
      };
    return null;
  }, [onAbortClicked, isReview, isLoading]);


  const getSeedStepNextBtn = useCallback(
    (): VLPrimaryButtonProps | null => ({
      text: "Run Label propagation",
      type: "primary",
      disabled: !allClassesMeetLimit || selectedValues.length < 2,
      onClick: () => {
        setStep(2);
      },
      tooltip:
        selectedValues.length < 2
          ? "Select at least two classes to continue"
          : !allClassesMeetLimit
            ? `Select at least ${requiredSeedSamplesPerLabel} items per class to continue.`
            : undefined,
    }),
    [
      allClassesMeetLimit,
      selectedValues.length,
      requiredSeedSamplesPerLabel,
      setStep,
    ],
  );

  useEffect(() => {
    if (!flowId || !datasetId) return;
    fetchStats(datasetId, flowId);
  }, [datasetId, fetchStats, flowId, shouldFetch]);

  return [
    {
      key: 1,
      title: {
        text: "Manage Seed Classes & Samples",
        icon:
          selectedValues.length > 0 ? (
            <AddClassButton
              onClick={() => seedStepRef?.current?.handleOpenModal()}
            />
          ) : undefined,
      },
      content: (
        <SeedStep
          ref={seedStepRef}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
          options={options}
          loading={loading}
          error={error}
          progressLimit={progressLimit}
          selectedCard={selectedCard}
          onDelete={onDelete ?? (() => {})}
          onItemDelete={onItemDelete ?? (() => {})}
          classItemsMap={classItemsMap}
          setClassItemsMap={setClassItemsMap}
        />
      ),
      nextButton: getSeedStepNextBtn(),
      prevButton: null,
    },
    {
      key: 2,
      content: (
        <ProgressStep
          seedId={seedId}
          initialFlowId={flowId}
          stats={stats}
          loading={isLoading}
        />
      ),
      nextButton: getProgressStepNextBtn(),
      prevButton: getProgressStepPrevBtn(),
    },
  ];
};
